/**
 * AppInput — Material-style underline field: floating label, active line (center scale),
 * focus highlight pulse. Prefix, start/end icons, password toggle, OTPInput.
 * Animations run on the UI thread (Reanimated).
 */
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { View, Text, TextInput, TextInputProps, Platform, StyleSheet, ViewStyle, TextStyle, StyleProp } from 'react-native';
import Animated, { useSharedValue, useAnimatedStyle, withTiming, withSequence, interpolateColor, interpolate, Easing } from 'react-native-reanimated';
import { Clickable } from './Clickable';
import { SvgIcon } from './SvgIcon';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import { useTranslation } from '../../shared/i18n';
import { androidScaleFont, androidScaleSize } from '../../shared/theme/sharedStyles';

export type AppInputType = 'text' | 'mail' | 'tel' | 'password' | 'number' | 'textarea';

const tokens = appTheme.input;
const spacing = appTheme.spacing;
const typography = appTheme.typography;

const ROW_MIN_HEIGHT = androidScaleSize(52);
const FLOAT_LABEL_TOP_REST = androidScaleSize(15);
const FLOAT_LABEL_TOP_FLOAT = androidScaleSize(2);
const UNDERLINE_BASE_HEIGHT = 1;
const UNDERLINE_ACTIVE_HEIGHT = 2;
const FLOAT_LABEL_FONT_REST = androidScaleFont(16);
const FLOAT_LABEL_FONT_FLOAT = androidScaleFont(12);
const FLOAT_LABEL_LINE_REST = androidScaleFont(20);
const FLOAT_LABEL_LINE_FLOAT = androidScaleFont(16);
const OTP_BOX_WIDTH = androidScaleSize(48);
const OTP_BOX_HEIGHT = androidScaleSize(56);
const OTP_FONT_SIZE = androidScaleFont(24);
const FLOAT_DURATION_MS = 200;
const HIGHLIGHT_PULSE_MS = 320;
/**
 * At rest the floating label sits at `FLOAT_LABEL_TOP_REST`; the prefix column is bottom-aligned
 * with the value line. Without a nudge, +code sits with the input baseline while the label reads
 * higher — add this to the rest `translateY` (with `pad_step`) so prefix matches the label band,
 * then animate to 0 when floated (focused/filled) to match the digits.
 */
const PREFIX_REST_LABEL_BAND_OFFSET = 10;

export function createInputStyles(c: ThemeColors) {
  return StyleSheet.create({
    container: {
      width: '100%',
      marginBottom: spacing.lg,
      overflow: 'visible',
    },
    rootColumn: {
      width: '100%',
    },
    inputRow: {
      flexDirection: 'row',
      alignItems: 'stretch',
      minHeight: ROW_MIN_HEIGHT,
      backgroundColor: 'transparent',
      position: 'relative',
    },
    inputRowWithPrefix: {
      paddingLeft: 0,
    },
    /** Prefix (e.g. +965) — bottom-aligned like fieldStack; inner uses same float padding as input. */
    leftAccessorySlot: {
      alignSelf: 'stretch',
      justifyContent: 'flex-end',
      alignItems: 'center',
      minWidth: 56,
      paddingStart: tokens.paddingHorizontal,
      paddingEnd: spacing.sm,
      paddingBottom: spacing.xs,
    },
    /** Mirrors TextInput paddingVertical so prefix baseline tracks the editable line. */
    leftAccessoryInputMirror: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: Platform.OS === 'ios' ? spacing.xs : spacing.xxs,
    },
    /** Textarea fieldStack is top-aligned; keep prefix in sync when both are used. */
    leftAccessorySlotTextarea: {
      justifyContent: 'flex-start',
      paddingTop: spacing.sm,
      paddingBottom: 0,
    },
    iconColumn: {
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: spacing.xs,
      minWidth: 36,
    },
    iconColumnStart: {
      paddingStart: tokens.paddingHorizontal * 0.5,
    },
    iconColumnEnd: {
      paddingEnd: tokens.paddingHorizontal * 0.5,
    },
    /** Text + floating label stack */
    fieldStack: {
      flex: 1,
      justifyContent: 'flex-end',
      minHeight: ROW_MIN_HEIGHT,
      paddingBottom: spacing.xs,
      position: 'relative',
      overflow: 'visible',
    },
    fieldStackTextarea: {
      minHeight: 88,
      justifyContent: 'flex-start',
      paddingTop: spacing.sm,
    },
    fieldStackAfterPrefix: {
      paddingStart: spacing.sm,
    },
    input: {
      ...typography.body,
      color: c.text,
      paddingVertical: Platform.OS === 'ios' ? spacing.xs : spacing.xxs,
      paddingHorizontal: 0,
      margin: 0,
      minHeight: 24,
      textAlignVertical: 'center',
      backgroundColor: 'transparent',
      borderWidth: 0,
    },
    inputTextarea: {
      minHeight: 72,
      paddingTop: spacing.sm,
      textAlignVertical: 'top',
    },
    floatingLabel: {
      position: 'absolute',
      left: 0,
      right: 0,
      zIndex: 2,
      letterSpacing: 0.15,
      ...typography.body,
    },
    underlineStack: {
      width: '100%',
      height: UNDERLINE_ACTIVE_HEIGHT,
      marginTop: 0,
      position: 'relative',
    },
    underlineBase: {
      ...StyleSheet.absoluteFillObject,
      height: UNDERLINE_BASE_HEIGHT,
      top: UNDERLINE_ACTIVE_HEIGHT - UNDERLINE_BASE_HEIGHT,
      backgroundColor: c.border,
    },
    underlineActive: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: UNDERLINE_ACTIVE_HEIGHT,
      borderRadius: 1,
    },
    highlightOverlay: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: c.primary,
      pointerEvents: 'none',
      zIndex: 0,
    },
    inputWrap: {
      zIndex: 1,
      width: '100%',
    },
    passwordToggle: {
      padding: spacing.xxs,
      alignItems: 'center',
      justifyContent: 'center',
      alignSelf: 'center',
    },
    rightAccessory: {
      justifyContent: 'center',
      alignItems: 'center',
      paddingStart: spacing.xs,
    },
    footer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingTop: spacing.xs,
      paddingHorizontal: spacing.xxs,
      gap: spacing.sm,
    },
    helper: {
      flex: 1,
      ...typography.caption,
    },
    error: {
      flex: 1,
      ...typography.bodySmall,
      fontWeight: '500',
    },
    counter: {
      ...typography.caption,
      color: c.textSecondary,
    },
    counterError: {
      color: c.error,
    },
    otpContainer: {
      flexDirection: 'row',
      justifyContent: 'center',
      marginBottom: appTheme.spacing.xl,
    },
    otpBox: {
      width: OTP_BOX_WIDTH,
      height: OTP_BOX_HEIGHT,
      borderWidth: 1,
      borderRadius: appTheme.borderRadius.md,
      marginHorizontal: androidScaleSize(6),
      fontSize: OTP_FONT_SIZE,
      fontWeight: '600',
      textAlign: 'center',
      padding: 0,
    },
  });
}

export interface InputProps extends Omit<TextInputProps, 'style' | 'value' | 'onChangeText'> {
  label?: string;
  placeholder?: string;
  type?: AppInputType;
  icon?: string;
  iconPosition?: 'start' | 'end';
  disabled?: boolean;
  required?: boolean;
  readonly?: boolean;
  error?: string;
  errorMessage?: string;
  helperText?: string;
  maxLength?: number;
  showCharCounter?: boolean;
  rows?: number;
  value?: string;
  onChangeText?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  leftAccessory?: React.ReactNode;
  rightAccessory?: React.ReactNode;
  containerStyle?: StyleProp<ViewStyle>;
  inputStyle?: StyleProp<TextStyle>;
  wrapperStyle?: StyleProp<ViewStyle>;
  /**
   * `selector`: read-only field styled like a text input (underline, full opacity) for picker triggers.
   * Renders non-editable without the disabled appearance unless `disabled` is true.
   */
  presentation?: 'default' | 'selector';
  accessible?: boolean;
}

const ease_out = Easing.out(Easing.cubic);

const InputComponent = (
  {
    label: label_prop,
    placeholder = '',
    type = 'text',
    icon,
    iconPosition = 'start',
    disabled = false,
    required = false,
    readonly = false,
    error,
    errorMessage: error_message_prop,
    helperText,
    maxLength,
    showCharCounter = false,
    rows = 4,
    value = '',
    onChangeText,
    onFocus,
    onBlur,
    leftAccessory,
    rightAccessory,
    containerStyle,
    inputStyle,
    wrapperStyle,
    presentation = 'default',
    accessible = true,
    secureTextEntry = false,
    ...rest
  }: InputProps,
  ref: React.Ref<TextInput>,
) => {
  const { t } = useTranslation();
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createInputStyles(colors), [colors]);
  const [is_focused, setIsFocused] = useState(false);
  const [show_password, setShowPassword] = useState(false);

  const is_selector = presentation === 'selector';
  const error_message = error_message_prop ?? error ?? undefined;
  const has_value = value != null && value !== '';
  const has_error = !!error_message;
  const is_textarea = type === 'textarea';
  const show_password_toggle = type === 'password' || secureTextEntry;

  const label_trim = label_prop?.trim() ?? '';
  const placeholder_trim = placeholder?.trim() ?? '';

  /** Text shown by the floating label (label wins; else placeholder as sole hint). */
  const float_text = label_trim || placeholder_trim;
  const has_floating_ui = float_text.length > 0;
  const should_float = has_floating_ui && (is_focused || has_value);

  /**
   * Native placeholder must never duplicate the resting floating text.
   * - Plain field (no MD label): use placeholder normally.
   * - label + optional hint: hint only when floated (focused or filled).
   * - placeholder-only MD: float text is the hint; TextInput placeholder stays empty.
   */
  const input_placeholder = useMemo(() => {
    if (!has_floating_ui) {
      return placeholder_trim;
    }
    if (label_trim) {
      return should_float ? placeholder_trim : '';
    }
    return '';
  }, [has_floating_ui, label_trim, placeholder_trim, should_float]);

  const show_start_icon = !!icon && iconPosition === 'start' && !leftAccessory;
  const show_end_icon = !!icon && iconPosition === 'end' && !show_password_toggle && !rightAccessory;

  const float_sv = useSharedValue(0);
  const focus_sv = useSharedValue(0);
  const error_sv = useSharedValue(0);
  const disabled_sv = useSharedValue(0);
  const highlight_sv = useSharedValue(0);
  const shake_x = useSharedValue(0);

  useEffect(() => {
    float_sv.value = withTiming(should_float ? 1 : 0, { duration: FLOAT_DURATION_MS, easing: ease_out });
  }, [should_float, float_sv]);

  useEffect(() => {
    focus_sv.value = withTiming(is_focused && !disabled ? 1 : 0, { duration: FLOAT_DURATION_MS, easing: ease_out });
  }, [is_focused, disabled, focus_sv]);

  useEffect(() => {
    error_sv.value = withTiming(has_error ? 1 : 0, { duration: FLOAT_DURATION_MS, easing: ease_out });
  }, [has_error, error_sv]);

  useEffect(() => {
    disabled_sv.value = withTiming(disabled || readonly ? 1 : 0, { duration: 160, easing: ease_out });
  }, [disabled, readonly, disabled_sv]);

  useEffect(() => {
    if (is_focused && !disabled) {
      highlight_sv.value = withSequence(
        withTiming(0.14, { duration: 45, easing: Easing.out(Easing.quad) }),
        withTiming(0, { duration: HIGHLIGHT_PULSE_MS, easing: Easing.out(Easing.cubic) }),
      );
    }
  }, [is_focused, disabled, highlight_sv]);

  useEffect(() => {
    if (has_error) {
      shake_x.value = withSequence(
        withTiming(-5, { duration: 40 }),
        withTiming(5, { duration: 40 }),
        withTiming(-3, { duration: 35 }),
        withTiming(3, { duration: 35 }),
        withTiming(0, { duration: 35 }),
      );
    }
  }, [has_error, shake_x]);

  const animated_highlight_style = useAnimatedStyle(() => ({
    opacity: highlight_sv.value,
  }));

  const animated_underline_active_style = useAnimatedStyle(() => {
    'worklet';
    const active = focus_sv.value * (1 - disabled_sv.value);
    const line_color = interpolateColor(error_sv.value, [0, 1], [colors.primary, colors.error]);
    return {
      transform: [{ scaleX: interpolate(active, [0, 1], [0, 1]) }],
      backgroundColor: line_color,
      opacity: interpolate(disabled_sv.value, [0, 1], [1, 0.35]),
    };
  }, [colors.primary, colors.error]);

  const animated_underline_base_style = useAnimatedStyle(() => {
    'worklet';
    const base = interpolateColor(error_sv.value, [0, 1], [colors.border, colors.error]);
    return {
      backgroundColor: interpolateColor(disabled_sv.value, [0, 1], [base, colors.borderLight ?? colors.border]),
      opacity: interpolate(disabled_sv.value, [0, 1], [1, 0.55]),
    };
  }, [colors.border, colors.error, colors.borderLight]);

  const animated_float_label_style = useAnimatedStyle(() => {
    'worklet';
    const t = float_sv.value;
    return {
      top: interpolate(t, [0, 1], [FLOAT_LABEL_TOP_REST, FLOAT_LABEL_TOP_FLOAT]),
      fontSize: interpolate(t, [0, 1], [FLOAT_LABEL_FONT_REST, FLOAT_LABEL_FONT_FLOAT]),
      lineHeight: interpolate(t, [0, 1], [FLOAT_LABEL_LINE_REST, FLOAT_LABEL_LINE_FLOAT]),
    };
  });

  const animated_float_label_color = useAnimatedStyle(() => {
    'worklet';
    const t = float_sv.value;
    const from_err = interpolateColor(error_sv.value, [0, 1], [colors.textSecondary, colors.error]);
    return {
      color: interpolateColor(t, [0, 1], [from_err, colors.primary]),
      opacity: interpolate(disabled_sv.value, [0, 1], [1, 0.5]),
    };
  }, [colors.textSecondary, colors.primary, colors.error]);

  const pad_top_rest = has_floating_ui ? androidScaleSize(20) : androidScaleSize(8);
  const pad_top_float = has_floating_ui ? androidScaleSize(22) : androidScaleSize(6);
  const animated_input_padding = useAnimatedStyle(
    () => ({
      paddingTop: interpolate(float_sv.value, [0, 1], [pad_top_rest, pad_top_float]),
    }),
    [has_floating_ui, pad_top_rest, pad_top_float],
  );

  /**
   * Same `paddingTop` as `inputWrap`. `translateY`: (1) `-pad_step` counters flex-end padding growth;
   * (2) `-PREFIX_REST_LABEL_BAND_OFFSET` at rest aligns prefix with the MD label row (not the value
   * line); both go to 0 when floated so prefix stays level with typed text.
   */
  const animated_prefix_style = useAnimatedStyle(() => {
    'worklet';
    const t = float_sv.value;
    const pad = interpolate(t, [0, 1], [pad_top_rest, pad_top_float]);
    const pad_step = pad_top_float - pad_top_rest;
    const rest_ty = -(pad_step + PREFIX_REST_LABEL_BAND_OFFSET);
    const ty = has_floating_ui ? interpolate(t, [0, 1], [rest_ty, 0]) : 0;
    return {
      paddingTop: pad,
      transform: [{ translateY: ty }],
    };
  }, [has_floating_ui, pad_top_rest, pad_top_float]);

  const shake_style = useAnimatedStyle(() => ({
    transform: [{ translateX: shake_x.value }],
  }));

  const effective_type: AppInputType = (type === 'password' || secureTextEntry) && show_password ? 'text' : type === 'password' ? 'password' : secureTextEntry ? 'password' : type;

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    onFocus?.();
  }, [onFocus]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    onBlur?.();
  }, [onBlur]);

  const togglePasswordVisibility = useCallback(() => {
    if (disabled) {
      return;
    }
    setShowPassword(prev => !prev);
  }, [disabled]);

  const character_count = value?.length ?? 0;
  const is_password_hidden = (type === 'password' || secureTextEntry) && !show_password;

  const keyboard_type =
    effective_type === 'mail' ? 'email-address' : effective_type === 'tel' ? 'phone-pad' : effective_type === 'number' ? 'numeric' : 'default';

  const icon_color = has_error ? colors.error : is_focused ? colors.text : colors.textSecondary;

  const text_input_editable = !disabled && !readonly && !is_selector;

  return (
    <View style={[styles.container, containerStyle]} pointerEvents={is_selector ? 'none' : 'auto'}>
      <Animated.View style={[styles.rootColumn, shake_style, wrapperStyle]}>
        <View style={[styles.inputRow, !!leftAccessory && styles.inputRowWithPrefix]} pointerEvents={is_selector ? 'none' : 'auto'}>
          {leftAccessory ? (
            <View
              pointerEvents={is_selector ? 'none' : 'auto'}
              style={[styles.leftAccessorySlot, is_textarea && styles.leftAccessorySlotTextarea]}
              collapsable={false}>
              <Animated.View style={animated_prefix_style}>
                <View style={styles.leftAccessoryInputMirror}>{leftAccessory}</View>
              </Animated.View>
            </View>
          ) : null}

          {show_start_icon ? (
            <View style={[styles.iconColumn, styles.iconColumnStart]} pointerEvents={is_selector ? 'none' : 'auto'}>
              <SvgIcon name={icon!} size={22} color={icon_color} />
            </View>
          ) : null}

          <View
            pointerEvents={is_selector ? 'none' : 'auto'}
            style={[
              styles.fieldStack,
              is_textarea && styles.fieldStackTextarea,
              !!leftAccessory && styles.fieldStackAfterPrefix,
              { flex: 1 },
            ]}>
            <Animated.View
              pointerEvents="none"
              style={[styles.highlightOverlay, animated_highlight_style, { borderRadius: 6, marginHorizontal: -6, marginVertical: -4 }]}
            />

            {has_floating_ui ? (
              <Animated.Text
                style={[styles.floatingLabel, animated_float_label_style, animated_float_label_color]}
                numberOfLines={1}
                pointerEvents="none">
                {float_text}
                {required ? ' *' : ''}
              </Animated.Text>
            ) : null}

            <Animated.View style={[styles.inputWrap, animated_input_padding]} pointerEvents={is_selector ? 'none' : 'auto'}>
              {!is_textarea ? (
                <TextInput
                  ref={ref}
                  value={value}
                  placeholder={input_placeholder}
                  placeholderTextColor={colors.textLight}
                  onChangeText={onChangeText}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  editable={text_input_editable}
                  accessible={accessible}
                  maxLength={maxLength}
                  keyboardType={keyboard_type}
                  secureTextEntry={is_password_hidden}
                  style={[styles.input, Platform.OS === 'android' && { includeFontPadding: false }, { color: colors.text }, inputStyle]}
                  {...rest}
                  pointerEvents={is_selector ? 'none' : 'auto'}
                />
              ) : (
                <TextInput
                  ref={ref}
                  value={value}
                  placeholder={input_placeholder}
                  placeholderTextColor={colors.textLight}
                  onChangeText={onChangeText}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  editable={text_input_editable}
                  accessible={accessible}
                  maxLength={maxLength}
                  multiline
                  numberOfLines={rows}
                  style={[styles.input, styles.inputTextarea, { color: colors.text }, inputStyle]}
                  {...rest}
                  pointerEvents={is_selector ? 'none' : 'auto'}
                />
              )}
            </Animated.View>
          </View>

          {show_end_icon ? (
            <View style={[styles.iconColumn, styles.iconColumnEnd]} pointerEvents={is_selector ? 'none' : 'auto'}>
              <SvgIcon name={icon!} size={22} color={icon_color} />
            </View>
          ) : null}

          {rightAccessory ? (
            <View style={styles.rightAccessory} pointerEvents={is_selector ? 'none' : 'auto'}>
              {rightAccessory}
            </View>
          ) : null}
          {show_password_toggle ? (
            <Clickable
              style={styles.passwordToggle}
              onPress={togglePasswordVisibility}
              disabled={disabled}
              hitSlop={appTheme.hitSlop.small}
              accessibilityLabel={show_password ? t('common.showPassword') : t('common.hidePassword')}
              accessibilityRole="button">
              <SvgIcon name={show_password ? 'noeye' : 'eye'} size={22} color={colors.textSecondary} />
            </Clickable>
          ) : null}
        </View>

        <View style={styles.underlineStack} pointerEvents={is_selector ? 'none' : 'auto'}>
          <Animated.View
            style={[
              {
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: UNDERLINE_ACTIVE_HEIGHT - UNDERLINE_BASE_HEIGHT,
                height: UNDERLINE_BASE_HEIGHT,
              },
              animated_underline_base_style,
            ]}
          />
          <Animated.View style={[styles.underlineActive, animated_underline_active_style]} />
        </View>
      </Animated.View>

      {(helperText || error_message || (showCharCounter && maxLength != null)) && (
        <View style={styles.footer}>
          {!error_message && helperText ? <Text style={[styles.helper, { color: colors.textSecondary }]}>{helperText}</Text> : null}
          {error_message ? <Text style={[styles.error, { color: colors.error }]}>{error_message}</Text> : null}
          {showCharCounter && maxLength != null ? (
            <Text style={[styles.counter, has_error && styles.counterError]} numberOfLines={1}>
              {character_count} / {maxLength}
            </Text>
          ) : null}
        </View>
      )}
    </View>
  );
};

export const Input = React.forwardRef<TextInput, InputProps>(InputComponent);

export interface OTPInputProps {
  value: string;
  onChangeText: (value: string) => void;
  error?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  /** Fires when any digit box gains focus — for auth keyboard avoidance (`on_field_focus`). */
  onFocus?: () => void;
}

const OTP_DIGIT_COUNT = 6;

export const OTPInput: React.FC<OTPInputProps> = ({ value, onChangeText, error = false, containerStyle, onFocus }) => {
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createInputStyles(colors), [colors]);
  const input_refs = useRef<(TextInput | null)[]>([]);

  const digits = value.split('').concat(Array(OTP_DIGIT_COUNT).fill('')).slice(0, OTP_DIGIT_COUNT) as string[];

  const handle_change = useCallback(
    (raw: string, index: number) => {
      const chars = raw.replace(/\D/g, '').split('');
      if (chars.length > 1) {
        const next = [...digits];
        let i = index;
        for (const c of chars) {
          if (i < OTP_DIGIT_COUNT) {
            next[i] = c;
          }
          i += 1;
        }
        const new_value = next.join('').slice(0, OTP_DIGIT_COUNT);
        onChangeText(new_value);
        const focus_index = Math.min(index + chars.length, OTP_DIGIT_COUNT - 1);
        input_refs.current[focus_index]?.focus();
        return;
      }
      const char = /^\d$/.test(chars[0] ?? '') ? chars[0] : '';
      const next = [...digits];
      next[index] = char;
      onChangeText(next.join(''));
      if (char && index < OTP_DIGIT_COUNT - 1) {
        input_refs.current[index + 1]?.focus();
      }
    },
    [digits, onChangeText],
  );

  const handle_key_press = useCallback(
    (e: any, index: number) => {
      if (e.nativeEvent.key === 'Backspace' && !digits[index] && index > 0) {
        input_refs.current[index - 1]?.focus();
      }
    },
    [digits],
  );

  return (
    <View style={[styles.otpContainer, containerStyle]}>
      {digits.map((digit, index) => (
        <TextInput
          key={index}
          ref={r => {
            input_refs.current[index] = r;
          }}
          style={[
            styles.otpBox,
            {
              color: colors.text,
              borderColor: error ? colors.error : digit ? colors.primary : colors.border,
              backgroundColor: error ? undefined : digit ? `${colors.primary}10` : colors.surface,
            },
          ]}
          value={digit}
          onChangeText={v => handle_change(v, index)}
          onKeyPress={e => handle_key_press(e, index)}
          onFocus={onFocus}
          keyboardType="number-pad"
          maxLength={1}
          selectTextOnFocus
        />
      ))}
    </View>
  );
};

export default Input;
export { Input as AppInput };
